import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { AiFillDashboard } from "react-icons/ai";
import { FaPeopleArrows } from "react-icons/fa6";
import { HiOutlineHome } from "react-icons/hi2";
import { TbLogs } from "react-icons/tb";
import { IoSettingsOutline } from "react-icons/io5";
import { MdCreateNewFolder, MdOutlinePayments } from "react-icons/md";
import { SiWebmoney } from "react-icons/si";
import { VscGitPullRequestCreate } from "react-icons/vsc";
import { motion, AnimatePresence } from "framer-motion";
import User from "../../users/User";
import { AuthContext } from "../../context/AuthContext";

const Sidebar = ({ isExpanded, setIsExpanded }) => {
  const { authState } = useContext(AuthContext);
  const location = useLocation();

  const isAdmin =
    authState.isAuthenticated &&
    authState.user &&
    authState.user.role === "admin";

  const toggleSidebar = () => setIsExpanded(!isExpanded);

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-800 to-gray-900 text-gray-100 shadow-lg"
      animate={{ width: isExpanded ? 220 : 80 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <div className="flex flex-col h-full">
        <div className="p-4 flex items-center justify-between">
          <AnimatePresence>
            {isExpanded && (
              <motion.div 
                className="flex items-center gap-2" 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <SiWebmoney className="text-indigo-400 text-2xl" />
                <span className="font-bold text-xl tracking-wide">intelleX</span>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.button 
            onClick={toggleSidebar} 
            className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {isExpanded ? "←" : "→"}
          </motion.button>
        </div>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="px-4 py-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <User />
            </motion.div>
          )}
        </AnimatePresence>

        <nav className="flex-1 overflow-y-auto py-4 space-y-2">
          <SidebarLink to="/dashboard" icon={<AiFillDashboard />} label="Dashboard" isExpanded={isExpanded} isActive={location.pathname === "/dashboard"} />
          <SidebarLink to="/" icon={<HiOutlineHome />} label="Home" isExpanded={isExpanded} isActive={location.pathname === "/"} />
          <SidebarLink to="/contracts" icon={<MdOutlinePayments />} label="All Contracts" isExpanded={isExpanded} isActive={location.pathname === "/contracts"} />
          {isAdmin && (
            <>
              <SidebarLink to="/create-contract" icon={<MdCreateNewFolder />} label="Add Contract" isExpanded={isExpanded} isActive={location.pathname === "/create-contract"} />
              <SidebarLink to="/generate-contract" icon={<VscGitPullRequestCreate />} label="Generate" isExpanded={isExpanded} isActive={location.pathname === "/generate-contract"} />
              <SidebarLink to="/users" icon={<FaPeopleArrows />} label="Users" isExpanded={isExpanded} isActive={location.pathname === "/users"} />
            </>
          )}
        </nav>

        {isAdmin && (
          <div className="mt-auto border-t border-gray-700 py-4 space-y-2">
            <SidebarLink to="/logs" icon={<TbLogs />} label="Logs" isExpanded={isExpanded} isActive={location.pathname === "/logs"} />
            <SidebarLink to="/settings" icon={<IoSettingsOutline />} label="Settings" isExpanded={isExpanded} isActive={location.pathname === "/settings"} />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const SidebarLink = ({ to, icon, label, isExpanded, isActive }) => (
  <Link to={to}>
    <motion.div
      className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors duration-200 ${
        isActive ? "bg-indigo-600 text-white" : "text-gray-300 hover:bg-gray-700"
      }`}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
    >
      {React.cloneElement(icon, { className: "text-xl" })}
      <AnimatePresence>
        {isExpanded && (
          <motion.span 
            className="font-medium text-sm"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  </Link>
);

export default Sidebar;
